/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useContext, useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  Button,
  FormGroup,
  Input,
  Label
} from 'reactstrap';
import { createMultipleOnboarding } from '../../actions/initiative';
import { Loader } from '@g17eco/atoms/loader';
import { companyTrackerUserRoles, portfolioTrackerUserRoles, UserRoles } from '@constants/user';
import { getChecked, handleRoleRadioChange } from '@utils/user';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { isValidEmail } from '../../utils';
import { generateUrl } from '../util';
import './styles.scss'
import { EmailChipsInput } from './EmailChipsInput';
import { addDate, getTimeUnitOptions } from '../../utils/date';
import { ACTION, UserInvitationFormContext, type ExpirationUnit } from './UserInvitationFormContainer';
import { addSiteAlert, SiteAlertColors } from '../../slice/siteAlertsSlice';
import { useAppDispatch, useAppSelector } from '../../reducers';
import { FeaturePermissions } from '../../services/permissions/FeaturePermissions';
import { getRootConfig } from '@selectors/globalData';
import { QUESTION } from '@constants/terminology';
import { RouteInterfaceMin } from '@g17eco/types/routes';
import { BasicAlert } from '@g17eco/molecules/alert';
import { useGetInitiativeUsersQuery } from '@api/manage-users';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';

interface UserInvitationProps {
  initiativeId: string;
  handleReload: () => void;
  baseRoute: RouteInterfaceMin;
}

const UserInvitationForm = ({ initiativeId, handleReload, baseRoute }: UserInvitationProps) => {
  const appDispatch = useAppDispatch();
  const history = useHistory();
  const match = useRouteMatch<{ [key: string]: string }>();
  const { emailInput, emails, errorMessage, hasExpiration, expirationAmount, expirationUnit, dispatch } =
    useContext(UserInvitationFormContext);

  const [isSaving, setSaving] = React.useState(false);
  const [selectedRoles, setRoles] = React.useState<UserRoles[]>([UserRoles.Viewer]);
  // @TODO: should this include subsidiaries? Seems likely?
  const { data, isFetching } = useGetInitiativeUsersQuery({ initiativeId, includeSubsidiaries: false });

  const isPortfolioTracker = match.params.portfolioId ? true : false;
  const userRoles = isPortfolioTracker ? portfolioTrackerUserRoles : companyTrackerUserRoles;
  const hasValidEmails = emailInput ? isValidEmail(emailInput) : emails.length > 0;
  const isExpirationValid = !hasExpiration || (Number(expirationAmount) > 0 && expirationUnit);
  const canSubmit = !isSaving && hasValidEmails && selectedRoles.length > 0 && isExpirationValid;
  const rootConfig = useAppSelector(getRootConfig);
  const canAccessVerification = FeaturePermissions.canAccessVerification(rootConfig);
  const userLimit = FeaturePermissions.getLimitUsers(rootConfig);
  const nonStaffUserCount = useMemo(() => {
    if (!data) {
      return;
    }
    const activeNonStaffUsers = data.users.reduce((acc, user) => acc + (user.isStaff ? 0 : 1), 0);
    const pendingNonStaffUsers = data.invitations.length;
    return activeNonStaffUsers + pendingNonStaffUsers;
  }, [data]);

  const addingEmailCount = emails.length + (isValidEmail(emailInput) ? 1 : 0);

  const isLimitReached = () => {
    if (isPortfolioTracker || userLimit === 0) {
      return false;
    }
    if (nonStaffUserCount === undefined) {
      return true;
    }
    if (addingEmailCount) {
      return nonStaffUserCount + addingEmailCount > userLimit;
    }
    return nonStaffUserCount >= userLimit;
  };

  if (isFetching) {
    return <Loader />;
  }

  const setRootUrl = () => {
    history.push(generateUrl(baseRoute, { initiativeId, portfolioId: initiativeId }));
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const role = e.target.value as UserRoles;
    const checked = e.target.checked;
    setRoles((prevRoles) => handleRoleRadioChange(role, checked ? 'add' : 'remove', prevRoles));
  };

  const onChangeEmailInput = (e: React.ChangeEvent<any>) => {
    dispatch({
      type: ACTION.SET_EMAIL_INPUT,
      payload: e.target.value,
    });
  };

  const onKeyDown = (e: React.KeyboardEvent<any>) => {
    if (['Enter', 'Tab', ','].includes(e.key)) {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleAdd = () => {
    dispatch({
      type: ACTION.ADD_EMAIL,
    });
    dispatch({
      type: ACTION.SET_EMAIL_INPUT,
      payload: '',
    });
  };

  const handleDelete = (item: string) => {
    dispatch({
      type: ACTION.DELETE_EMAIL,
      payload: emails.find((email) => email === item),
    });
  };

  const calculateExpirationDate = (): Date | undefined => {
    if (!hasExpiration || !expirationAmount || !expirationUnit) {
      return undefined;
    }
    return addDate(new Date(), expirationAmount, expirationUnit);
  };

  const handleSubmit = async () => {
    if (isLimitReached()) {
      return appDispatch(
        addSiteAlert({
          content:
            'Cannot add more users as you have reached the limit on your account. Please contact support to increase your limit',
          color: SiteAlertColors.Danger,
        }),
      );
    }

    if (!hasValidEmails) {
      dispatch({
        type: ACTION.SET_ERROR_MESSAGE,
        payload: 'The e-mails you typed is invalid. Please check and try again.',
      });
      return;
    }
    dispatch({
      type: ACTION.SET_ERROR_MESSAGE,
      payload: '',
    });

    const inviteEmails = Array.from(new Set([...emails, emailInput])).filter((email) => email);
    const expirationDate = calculateExpirationDate();

    setSaving(true);
    try {
      const onboardings = await createMultipleOnboarding(initiativeId, inviteEmails, selectedRoles, expirationDate);
      if (Array.isArray(onboardings)) {
        await Promise.all(
          onboardings
            .filter((onboarding) => onboarding._id)
            .map((onboarding) => {
              const analytics = getAnalytics();
              return analytics.track(AnalyticsEvents.UserInvited, {
                onboardId: onboarding._id,
                initiativeId: onboarding.initiativeId,
              });
            }),
        );
      }
      handleReload();
      setSaving(false);
      setRootUrl();
    } catch (error) {
      dispatch(
        addSiteAlert({
          content: `It was not possible to send the invitation. ${error.message ? `(${error.message})` : ''}`,
          color: SiteAlertColors.Danger,
        }),
      );
      handleReload();
      setSaving(false);
    }
  };

  const unitOptions = getTimeUnitOptions(expirationAmount);

  const selectedUnitOption = unitOptions.find((opt) => opt.value === expirationUnit);

  const renderRolesSelectors = () => {
    const adminRole = userRoles.find((item) => item.code === UserRoles.Manager);
    return (
      <>
        {!isPortfolioTracker && adminRole && (
          <FormGroup check className='py-0'>
            <Input
              type='checkbox'
              id={adminRole.code}
              name={adminRole.code}
              value={adminRole.code}
              onChange={handleRoleChange}
              checked={selectedRoles.includes(adminRole.code)}
            />
            <Label check for={adminRole.code}>
              <span className='strong'>{adminRole.label}</span>
              <div className='text-ThemeTextMedium'>{adminRole.description}</div>
            </Label>
          </FormGroup>
        )}
        {
          <>
            <hr className='mx-2 my-4 opacity-100 border-ThemeBorderDefault' />
            <div className='mt-4'>
              <h4 className='px-2'>User permissions</h4>
              {userRoles
                .filter((role) => !role.disabled && (isPortfolioTracker || role.code !== UserRoles.Manager))
                .map((role) => {
                  const disabled = role.requiresVerificationAccess && !canAccessVerification;
                  const id = `role_${role.code}`;
                  return (
                    <FormGroup check key={id} className='py-2' disabled={disabled}>
                      <Input
                        type='radio'
                        id={id}
                        name='user_roles'
                        value={role.code}
                        onChange={handleRoleChange}
                        checked={getChecked(role.code, selectedRoles)}
                        disabled={disabled}
                      />
                      <Label check for={id}>
                        <span className='strong'>
                          {role.code === UserRoles.Contributor || role.code === UserRoles.Verifier
                            ? `${role.label} only`
                            : role.label}
                          {disabled ? ` (Requires ${QUESTION.SINGULAR} verification workflow feature)` : null}
                        </span>
                        <div className='text-ThemeTextMedium'>{role.description}</div>
                      </Label>
                    </FormGroup>
                  );
                })}
            </div>
          </>
        }
      </>
    );
  };

  return (
    <>
      {errorMessage && <div className='alert alert-danger'>{errorMessage}</div>}
      {isLimitReached() ? (
        <BasicAlert type='warning'>
          You have reached your user limit of {userLimit}. Please contact us to increase your limit.
        </BasicAlert>
      ) : null}
      <div className='user-invitation-form-container'>
        <h4>Email address</h4>
        <FormGroup>
          <EmailChipsInput
            emailInput={emailInput}
            emails={emails}
            placeholder='Type user e-mail address and press Enter or Tab'
            onChangeEmailInput={onChangeEmailInput}
            onKeyDown={onKeyDown}
            handleDelete={handleDelete}
            handleAdd={handleAdd}
          />
        </FormGroup>
        {renderRolesSelectors()}
        <hr className='mx-2 my-4 opacity-100 border-ThemeBorderDefault' />
        <div className='mt-4'>
          <h4>Expiration Time</h4>
          <FormGroup check className='py-2'>
            <Input
              type='checkbox'
              id='has-expiration'
              checked={hasExpiration}
              onChange={(e) =>
                dispatch({
                  type: ACTION.SET_HAS_EXPIRATION,
                  payload: e.target.checked,
                })
              }
            />
            <Label check for='has-expiration'>
              Apply expiration date
            </Label>
          </FormGroup>

          <div className='mt-3 d-flex align-items-center gap-3'>
            <Input
              type='number'
              id='expiration-amount'
              min={1}
              value={expirationAmount || ''}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                dispatch({
                  type: ACTION.SET_EXPIRATION_AMOUNT,
                  payload: value > 0 ? value : 0,
                });
              }}
              onKeyDown={(e) => {
                if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                  e.preventDefault();
                }
              }}
              placeholder='Enter a positive value and select a unit'
              disabled={!hasExpiration}
            />
            <SelectFactory
              selectType={SelectTypes.SingleSelect}
              placeholder='Select the unit'
              options={unitOptions}
              value={selectedUnitOption}
              onChange={(option: any) =>
                dispatch({
                  type: ACTION.SET_EXPIRATION_UNIT,
                  payload: option?.value as ExpirationUnit,
                })
              }
              isDisabled={!hasExpiration}
            />
          </div>
        </div>
        <div className='mt-3 text-right'>
          <Button className='mr-3' disabled={isSaving} color='link-secondary' onClick={setRootUrl}>
            Cancel
          </Button>
          <Button color='primary' disabled={!canSubmit} onClick={handleSubmit} data-testid='invite-users-btn'>
            Invite user(s)
          </Button>
        </div>
      </div>
    </>
  );
};

export { UserInvitationForm };
